import { RefObject, useCallback, useEffect, useState } from 'react';
import Webcam from 'react-webcam';
import { logger } from '../../../utils/logger';

/**
 * Расширенные типы для поддержки torch функциональности
 */
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
    torch?: boolean;
}

interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
    torch?: boolean;
}

interface ExtendedMediaTrackConstraints extends MediaTrackConstraints {
    advanced?: ExtendedMediaTrackConstraintSet[];
}

/**
 * Хук для управления фонариком/вспышкой камеры устройства.
 * 
 * Ответственность:
 * - Проверка поддержки torch функциональности устройством
 * - Включение/выключение фонарика через MediaStreamTrack API
 * - Обработка ошибок и состояний недоступности
 * - Автоматический сброс состояния при смене камеры
 * 
 * @param webcamRef - ссылка на экземпляр react-webcam для доступа к MediaStreamTrack
 * @returns Состояние и методы управления фонариком
 * 
 * @remarks
 * Поддержка torch различается по платформам:
 * - Android Chrome: полная поддержка
 * - iOS Safari: ограниченная или отсутствующая поддержка
 * - Desktop: не поддерживается (нет вспышки)
 * 
 * @example
 * ```tsx
 * const webcamRef = useRef<Webcam>(null);
 * const { isTorchSupported, isTorchOn, toggleTorch } = useTorchControl(webcamRef);
 * 
 * return (
 *   <div>
 *     <Webcam ref={webcamRef} />
 *     {isTorchSupported && (
 *       <button onClick={toggleTorch}>
 *         {isTorchOn ? 'Выключить' : 'Включить'} фонарик
 *       </button>
 *     )}
 *   </div>
 * );
 * ```
 */
export function useTorchControl(webcamRef: RefObject<Webcam | null>) {
    const [isTorchSupported, setIsTorchSupported] = useState<boolean>(false);
    const [isTorchOn, setIsTorchOn] = useState<boolean>(false);
    const [isCheckingSupport, setIsCheckingSupport] = useState<boolean>(true);

    /**
     * Получает MediaStreamTrack от webcam для управления фонариком.
     */
    const getVideoTrack = useCallback((): MediaStreamTrack | null => {
        if (!webcamRef.current?.stream) {
            return null;
        }

        const videoTracks = webcamRef.current.stream.getVideoTracks();
        return videoTracks.length > 0 ? videoTracks[0] : null;
    }, [webcamRef]);

    /**
     * Проверяет поддержку torch функциональности устройством.
     */
    const checkTorchSupport = useCallback(() => {
        const track = getVideoTrack();
        
        if (!track) {
            logger.debug('Видео трек недоступен для проверки torch поддержки');
            setIsTorchSupported(false);
            setIsCheckingSupport(false);
            return;
        }

        try {
            // Проверяем наличие метода getCapabilities
            if (typeof track.getCapabilities !== 'function') {
                logger.debug('Метод getCapabilities недоступен');
                setIsTorchSupported(false);
                setIsCheckingSupport(false);
                return;
            }

            const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities;
            const torchSupported = Boolean(capabilities.torch);
            
            logger.debug('Проверка torch поддержки', { 
                supported: torchSupported, 
                capabilities: Object.keys(capabilities) 
            });
            
            setIsTorchSupported(torchSupported);
            setIsCheckingSupport(false);
        } catch (error) {
            logger.error('Ошибка при проверке torch поддержки', { error });
            setIsTorchSupported(false);
            setIsCheckingSupport(false);
        }
    }, [getVideoTrack]);

    /**
     * Включает или выключает фонарик.
     */
    const toggleTorch = useCallback(async () => {
        const track = getVideoTrack();
        
        if (!track || !isTorchSupported) {
            logger.warn('Попытка переключить фонарик при отсутствии поддержки');
            return;
        }

        try {
            const newTorchState = !isTorchOn;
            
            await track.applyConstraints({
                advanced: [{ torch: newTorchState }]
            } as ExtendedMediaTrackConstraints);
            
            setIsTorchOn(newTorchState);
            logger.debug('Фонарик переключен', { state: newTorchState });
        } catch (error) {
            logger.error('Ошибка переключения фонарика', { error, isTorchOn });
            
            // При ошибке сбрасываем состояние
            setIsTorchOn(false);
        }
    }, [getVideoTrack, isTorchSupported, isTorchOn]);

    /**
     * Сбрасывает состояние фонарика (например, при смене камеры).
     */
    const resetTorchState = useCallback(() => {
        setIsTorchOn(false);
        setIsTorchSupported(false);
        setIsCheckingSupport(true);
        logger.debug('Состояние фонарика сброшено');
    }, []);

    /**
     * Принудительно выключает фонарик без переключения состояния UI.
     * Используется при размонтировании компонента или смене камеры.
     */
    const forceTorchOff = useCallback(async () => {
        const track = getVideoTrack();
        
        if (!track || !isTorchOn) {
            return;
        }

        try {
            await track.applyConstraints({
                advanced: [{ torch: false }]
            } as ExtendedMediaTrackConstraints);
            logger.debug('Фонарик принудительно выключен');
        } catch (error) {
            logger.error('Ошибка принудительного выключения фонарика', { error });
        }
    }, [getVideoTrack, isTorchOn]);

    // Проверяем поддержку при изменении webcam stream
    useEffect(() => {
        if (webcamRef.current?.stream) {
            // Небольшая задержка для корректной инициализации stream
            const timeoutId = setTimeout(checkTorchSupport, 100);
            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [webcamRef.current?.stream, checkTorchSupport]);

    // Принудительно выключаем фонарик при размонтировании
    useEffect(() => {
        return () => {
            void forceTorchOff();
        };
    }, [forceTorchOff]);

    return {
        isTorchSupported,
        isTorchOn,
        isCheckingSupport,
        toggleTorch,
        resetTorchState,
        forceTorchOff
    };
}